import { useQuery } from '@apollo/client';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { Order_By } from '@openx/types/gqlTypes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../../../components/AuthContext/context';
import {
  GetOrganizationsListDocument,
  type GetOrganizationsListQuery,
  type GetOrganizationsListQueryVariables,
} from '../../../../../../../graphql/query/organizations/GetOrganizationsList.generated';
import { isInternalAdmin } from '../../../../../../../permissions/validators';

export function OnBehalfOf({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const { session } = useAuth();
  const isInternalOxAdmin = isInternalAdmin(session);
  const {
    data: { account: organizations = [] } = {},
    loading,
    error,
  } = useQuery<GetOrganizationsListQuery, GetOrganizationsListQueryVariables>(GetOrganizationsListDocument, {
    variables: {
      order_by: [{ name: Order_By.Asc }],
    },
  });

  const displayValue = useMemo(
    () => organizations.find(org => org.id === accountId)?.name || t('Unknown'),
    [accountId, organizations, t]
  );

  if (!isInternalOxAdmin) {
    return null;
  }

  return (
    <FormSection divider title={t('On Behalf Of')} rightColumnSize={RIGHT_COLUMN_WIDTH}>
      <Stack>
        <Typography variant="body2" color="textSecondary">
          {t('Organization for Deal')}
        </Typography>
        {loading && <Loader />}
        {error && <Alert severity="error">{t('Could not get organizations data from the server')}</Alert>}
        <Typography variant="body1">{displayValue}</Typography>
      </Stack>
    </FormSection>
  );
}
